import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CircularProgress, Stack } from "@mui/material";
import { toast } from "react-toastify";

import { AlertNote } from "./AlertNote";
import { Button } from "./Button";
import { useAuth } from "./contexts/AuthContext";
import { PLANS_ON_SALE, hasCommercialPlan } from "../utils/agencyFeatures";
import { createPortalSession } from "../services/billingService";
import { useStripeRedirect } from "../hooks/useStripeRedirect";
import { billingStatus, daysUntil, formatDate } from "../Pages/Plan/billingFormat";
import { PLAN_PATH } from "../Pages/Settings/settingsPaths";

const DISMISS_KEY = "markezy_billing_banner_dismissed";
const CANCEL_WARNING_DAYS = 7;
const TRIAL_WARNING_DAYS = 3;

const readDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
};

const writeDismissed = (value) => {
  try {
    sessionStorage.setItem(DISMISS_KEY, value);
  } catch {
    /* sin sessionStorage el banner vuelve a salir */
  }
};

// Estado de facturación que merece aviso global, o null. `past_due` no se puede cerrar.
const resolveState = (plan, status) => {
  if (status === "past_due") return { id: "past_due", dismissible: false };
  if (status) {
    const days = daysUntil(plan.current_period_end);
    if (days != null && days <= CANCEL_WARNING_DAYS) return { id: "cancel", dismissible: true };
    return null;
  }
  if (plan.is_trial && plan.days_remaining != null && plan.days_remaining <= TRIAL_WARNING_DAYS) {
    return { id: "trial", dismissible: true };
  }
  return null;
};

/**
 * Aviso global de pago fallido, cancelación próxima o fin de prueba. Solo para
 * agency_admin de agencias externas; en /plan ya lo muestra la tarjeta de estado.
 */
export function BillingStatusBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [redirecting, setRedirecting] = useState(false);
  const [dismissedKey, setDismissedKey] = useState(readDismissed);
  const { go: goToStripe, detailsDialog } = useStripeRedirect();

  const plan = user?.agencyPlan;
  if (!PLANS_ON_SALE || user?.role !== "agency_admin" || !hasCommercialPlan(user)) return null;
  if (location.pathname.startsWith(PLAN_PATH)) return null;

  const status = billingStatus(plan);
  const state = resolveState(plan, status);
  if (!state) return null;
  const stateKey = `${state.id}:${plan.current_period_end || plan.trial_ends_at || ""}`;
  if (state.dismissible && dismissedKey === stateKey) return null;

  const dismiss = () => {
    writeDismissed(stateKey);
    setDismissedKey(stateKey);
  };

  const openPortal = async () => {
    setRedirecting(true);
    try {
      if (!(await goToStripe(createPortalSession))) setRedirecting(false);
    } catch {
      toast.error("No se ha podido abrir la facturación. Inténtalo de nuevo.");
      setRedirecting(false);
    }
  };

  const planName = plan.plan_name;
  const downgrading = status === "downgrading";
  const buttonSx = { width: { xs: "100%", sm: "auto" } };

  const content = {
    past_due: {
      variant: "danger",
      text: (
        <>
          <strong>No hemos podido cobrar tu suscripción.</strong> Actualiza tu método de pago para mantener el plan{" "}
          {planName}. Si no se resuelve, tu agencia pasará al plan Free.
        </>
      ),
      action: (
        <Button size="sm" onClick={openPortal} disabled={redirecting} sx={{ ...buttonSx, gap: 1 }}>
          {redirecting ? (
            <>
              <CircularProgress size={14} color="inherit" /> Redirigiendo…
            </>
          ) : (
            "Actualizar pago"
          )}
        </Button>
      ),
    },
    cancel: {
      variant: "warning",
      text: (
        <>
          {downgrading
            ? `Tu plan ${planName} cambia a ${plan.scheduled_plan_name} el ${formatDate(plan.current_period_end)}.`
            : `Tu plan ${planName} finaliza el ${formatDate(plan.current_period_end)}. Después tu agencia pasará al plan Free (3 clientes con acceso completo).`}
        </>
      ),
      action: (
        <Button size="sm" variant="outline" onClick={() => navigate(PLAN_PATH)} sx={buttonSx}>
          {downgrading ? `Mantener ${planName}` : "Reactivar"}
        </Button>
      ),
    },
    trial: {
      variant: "info",
      text: (
        <>
          Tu prueba gratuita termina en {plan.days_remaining} {plan.days_remaining === 1 ? "día" : "días"}. Elige un plan
          para no perder el acceso completo a tus clientes.
        </>
      ),
      action: (
        <Button size="sm" onClick={() => navigate(PLAN_PATH)} sx={buttonSx}>
          Ver planes
        </Button>
      ),
    },
  }[state.id];

  return (
    <>
      <AlertNote
        variant={content.variant}
        sx={{ mb: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}
        action={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
            {content.action}
            {state.dismissible && (
              <Button variant="ghost" size="sm" onClick={dismiss} sx={buttonSx}>
                Cerrar
              </Button>
            )}
          </Stack>
        }
      >
        {content.text}
      </AlertNote>
      {detailsDialog}
    </>
  );
}
